import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  ButtonToolbar,
  Checkbox,
  FlexboxGrid,
  Form,
  SelectPicker,
} from "rsuite";

import "./StoreFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { RoleServices } from "../../../core/services/RoleServices";
import { get } from "lodash/fp";
import { StoreListParams } from "./StoreDataTypes";
import {
  messageTypeArr,
  roleTypeArr,
  statusObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { StoreServices } from "../../../core/services/StoreServices";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
}

export default function StoreFilters({
  onSubmit,
  onClear,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    code: "",
    mobile: "",
    region: "",
    group: "",
    unassigned_stores: "",
    contact_person: "",
    contact_person_phone: "",
    contact_person_email: "",
    prime_store_admin: "",
    prime_store_admin_email: "",
    prime_store_admin_phone: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    status: "",
  };
  const company = useSelector((state: any) => state.company);
  const [formData, setFormData] = useState<StoreListParams>(initSearchFormData);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [storeGroupList, setStoreGroupList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);
  const countryList = useSelector((state: any) => state.country.data);

  const handleUnassignedStoresCheck = (value: any, checked: any) => {
    let form = updateIn(formData, ["unassigned_stores"], checked ? value : "");
    if (checked) {
      form = updateIn(form, ["region"], "");
      form = updateIn(form, ["group"], "");
      setStoreGroupList([]);
    }
    setFormData(form);
  };

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    getRegionList();
  }, [company.id]);

  async function getRegionList(forComp?: number) {
    let compId: number = forComp ? forComp : getCompanyId();
    setRegionList([]);
    setStoreGroupList([]);
    if (!compId) {
      return;
    }
    let params: any = {};
    if (compId) {
      params.company = compId;
    }

    try {
      const res = await StoreServices.getRegions(params);
      const respData = get("data.data", res);
      if (respData.length) {
        setRegionList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }
  async function handleRegionChange(regionId: number) {
    try {
      setStoreGroupList([]);
      if (!regionId) {
        return;
      }
      const res = await StoreServices.getStoreGroups({
        region: regionId,
      });
      const respData = get("data.data", res);
      setStoreGroupList(respData);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function clearRegion() {
    handleFormChanges({ path: "region", value: "" });
    setStoreGroupList([]);
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  async function handleCountryChange(countryId: number) {
    try {
      const stateRes = await HelperServices.getAllValidStates({
        country: countryId,
      });
      setStateList(get("data.data", stateRes));
      resetSelectionFor("state");
      handleFormChanges({ path: "country", value: countryId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleStateChange(stateId: number) {
    try {
      const res = await HelperServices.getAllValidCities({ state: stateId });
      setCityList(res.data.data);
      resetSelectionFor("city");
      handleFormChanges({ path: "state", value: stateId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleCityChange(cityId: number) {
    try {
      const res = await HelperServices.getAllValidAreas({ city: cityId });
      setAreaList(res.data.data);
      resetSelectionFor("area");
      handleFormChanges({ path: "city", value: cityId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleAreaChange(areaId: number) {
    try {
      const res = await HelperServices.getAllValidBuildings({ area: areaId });
      setBuildingList(res.data.data);
      resetSelectionFor("building");
      handleFormChanges({ path: "area", value: areaId });
    } catch (error) {
      console.log(error);
    }
  }

  function clearSearch() {
    resetSelectionFor("country");
    setStoreGroupList([]);
    onClear();
    setFormData(initSearchFormData);
  }

  function resetSelectionFor(resetFor: string) {
    let resetData: Object = {};
    switch (resetFor) {
      case "country":
        resetData = {
          country: "",
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setStateList([]);
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "state":
        resetData = {
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "city":
        resetData = {
          city: "",
          area: "",
          building: "",
        };
        setAreaList([]);
        setBuildingList([]);
        break;
      case "area":
        resetData = {
          area: "",
          building: "",
        };
        setBuildingList([]);
        break;
      case "building":
        resetData = {
          building: "",
        };
        break;
    }
    let newFormData = {
      ...formData,
      ...resetData,
    };
    setFormData(newFormData);
  }

  return (
    <div>
      <div className="row mb-2">
        <ButtonToolbar>
          <Button onClick={handleSearchToggle}>{t("ADVANCE_SEARCH")}</Button>
        </ButtonToolbar>
      </div>
      {showFilter && (
        <div className="content">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            layout="inline"
          >
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="name">
                  <Form.ControlLabel>
                    {t("STORE")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="name" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="code">
                  <Form.ControlLabel>
                    {t("STORE")} {t("CODE")}
                  </Form.ControlLabel>
                  <Form.Control name="code" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="mobile">
                  <Form.ControlLabel>{t("MOBILE")}</Form.ControlLabel>
                  <Form.Control name="mobile" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="contact_person">
                  <Form.ControlLabel>
                    {t("CONTACT_PERSON_NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="contact_person" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="contact_person_phone">
                  <Form.ControlLabel>
                    {t("CONTACT_PERSON_PHONE")}
                  </Form.ControlLabel>
                  <Form.Control name="contact_person_phone" type="number" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="contact_person_email">
                  <Form.ControlLabel>
                    {t("CONTACT_PERSON_EMAIL")}
                  </Form.ControlLabel>
                  <Form.Control name="contact_person_email" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="prime_store_admin">
                  <Form.ControlLabel>
                    {t("MANAGER")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="prime_store_admin" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="prime_store_admin_phone">
                  <Form.ControlLabel>
                    {t("MANAGER")} {t("PHONE")}
                  </Form.ControlLabel>
                  <Form.Control name="prime_store_admin_phone" type="number" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="prime_store_admin_email">
                  <Form.ControlLabel>
                    {t("MANAGER")} {t("EMAIL")}
                  </Form.ControlLabel>
                  <Form.Control name="prime_store_admin_email" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="pincode">
                  <Form.ControlLabel>{t("PINCODE")}</Form.ControlLabel>
                  <Form.Control name="pincode" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="status">
                  <Form.ControlLabel>{t("STATUS")}</Form.ControlLabel>
                  <SelectPicker
                    data={statusObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "status", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "status", value: "" });
                    }}
                    name="status"
                    value={formData?.status}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="country">
                  <Form.ControlLabel>{t("Country")}</Form.ControlLabel>
                  <SelectPicker
                    data={countryList}
                    valueKey="id"
                    labelKey="country_name"
                    onSelect={(value, item) => {
                      handleCountryChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("country");
                    }}
                    name="country"
                    value={formData?.country}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="state">
                  <Form.ControlLabel>{t("STATE")}</Form.ControlLabel>
                  <SelectPicker
                    data={stateList}
                    valueKey="id"
                    labelKey="state_name"
                    onSelect={(value, item) => {
                      handleStateChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("state");
                    }}
                    name="state"
                    value={formData?.state}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="city">
                  <Form.ControlLabel>{t("CITY")}</Form.ControlLabel>
                  <SelectPicker
                    data={cityList}
                    valueKey="id"
                    labelKey="city_name"
                    onSelect={(value, item) => {
                      handleCityChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("city");
                    }}
                    name="city"
                    value={formData?.city}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="area">
                  <Form.ControlLabel>{t("AREA")}</Form.ControlLabel>
                  <SelectPicker
                    data={areaList}
                    valueKey="id"
                    labelKey="area_name"
                    onSelect={(value, item) => {
                      handleAreaChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("area");
                    }}
                    name="area"
                    value={formData?.area}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="building">
                  <Form.ControlLabel>{t("BUILDING")}</Form.ControlLabel>
                  <SelectPicker
                    data={buildingList}
                    valueKey="id"
                    labelKey="building_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "building", value });
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("building");
                    }}
                    name="building"
                    value={formData?.building}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="region">
                  <Form.ControlLabel>{t("REGION")}</Form.ControlLabel>
                  <SelectPicker
                    data={regionList}
                    valueKey="id"
                    labelKey="region_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "region", value });
                      handleRegionChange(value);
                    }}
                    onClean={(e: any) => {
                      clearRegion();
                    }}
                    name="region"
                    value={formData?.region}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="group">
                  <Form.ControlLabel>{t("GROUP")}</Form.ControlLabel>
                  <SelectPicker
                    data={storeGroupList}
                    valueKey="id"
                    labelKey="group_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "group", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "group", value: "" });
                    }}
                    name="group"
                    value={formData?.group}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="unassigned_stores">
                  <Form.ControlLabel>
                    {t("UNASSIGNED")} {t("STORES")}
                  </Form.ControlLabel>
                  <Checkbox
                    name="unassigned_stores"
                    value={1}
                    onChange={handleUnassignedStoresCheck}
                    checked={formData.unassigned_stores == "" ? false : true}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>

            <div className="filter-btn">
              <Button onClick={() => clearSearch()}>{t("CLEAR_FILTER")}</Button>
              <Button onClick={() => onSubmit(formData)}>{t("Search")}</Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
